<template>
    <div style="display: inline-block">
        <v-popper2
            class="no-flex"
            position="bottomEnd"
            style="width: auto;"
        >
            <template #trigger="{popper}">
                <v-icon-button @click="popper.toggle()">
                    <v-icon icon="actions"/>
                </v-icon-button>
            </template>
            <template #content="{popper}">
                <div class="popover">
                    <div class="popover-control">
                        <div class="popover-content popover-menu">
                            <p class="popover-menu-item" @click="triggerEditCustomer(popper)" v-if="$auth.customers.edit.isAllowed">Edit Customer</p>
                            <router-link class="popover-menu-item" :to="{name: 'add-order', params: {customerId: customer.id}}">Create Order</router-link>
                            <redeem-coupon :customer="customer" v-if="$auth.customers.edit.isAllowed" @close="popper.close()">
                                <template #trigger="{show}">
                                    <p class="popover-menu-item" @click="show">Coupons</p>
                                </template>
                            </redeem-coupon>
                            <p class="popover-menu-item" @click="triggerEditLeadSources(popper)" v-if="$auth.leadSources.edit.isAllowed">Lead Sources</p>
                            <p class="popover-menu-item" @click="triggerEditBlacklist(popper)" v-if="$auth.blacklists.edit.isAllowed">Blacklists</p>
                        </div>
                    </div>
                </div>
            </template>
        </v-popper2>
        <blacklist-modal
            v-show="isEditingBlacklist"
            @close="isEditingBlacklist = false"
            @update="addBlacklistsItem"
        />
        <edit-customer-modal
            :key="customer.updatedTime"
            v-show="isEditingCustomer"
            @close="isEditingCustomer = false"
            @input="updateCustomerInfo"
        />
        <lead-sources-modal
            v-if="isEditingLeadSources"
            :entry="leadSources"
            @close="isEditingLeadSources = false"
            @update="updateLeadSourcesInfo"
        />
    </div>
</template>

<script>
    import vPopper2 from '@/components/popper/v-popper2';
    import {
        vIcon,
        vIconButton,
    } from '@/components/form';
    import {mapState, mapActions} from 'vuex';
    import BlacklistModal from '@/modules/blacklists/blacklist-form-modal';
    import LeadSourceModel from '@/common/models/lead-source-model';
    import LeadSourcesModal from '@/components/lead-sources/lead-sources-modal';
    import EditCustomerModal from '@/modules/customers/components/customer-edit';
    import RedeemCoupon from '@/modules/orders/components/order-summary/redeem-coupon';
    import EmailMessageModel from '@/common/models/email-message-model';


    export default {
        provide() {
            return {
                sendEmailToCustomer: this.sendEmailToCustomer,
            };
        },
        components: {
            vIcon,
            vIconButton,
            vPopper2,
            BlacklistModal,
            LeadSourcesModal,
            EditCustomerModal,
            RedeemCoupon,
        },
        data() {
            return {
                isEditingLeadSources: false,
                isEditingBlacklist: false,
                isEditingCustomer: false,
                isAddingCoupons: false,
            };
        },
        computed: {
            ...mapState('customers', ['customer']),
            leadSources() {
                return new LeadSourceModel(this.customer.leadSource || {});
            },
        },
        watch: {
            customer() {
                this.isEditingCustomer = false;
                this.isEditingLeadSources = false;
            },
        },
        methods: {
            ...mapActions({
                setCustomer: 'customers/set',
                updateLeadSources: 'customers/updateLeadSources',
                addBlacklist: 'blacklists/add',
            }),
            triggerEditCustomer(popper) {
                this.isEditingCustomer = true;
                popper.close();
            },
            triggerEditBlacklist(popper) {
                this.isEditingBlacklist = true;
                popper.close();
            },
            triggerEditLeadSources(popper) {
                this.isEditingLeadSources = true;
                popper.close();
            },
            async updateCustomerInfo(data) {
                const params = {
                    id: this.customer.id,
                    data,
                    expand: 'leadSource,defaultPaymentInstrument',
                };
                try {
                    const {fields: customer} = await this.$api(({customers}) => customers.update(params));
                    this.setCustomer(customer);
                } catch (error) {
                    this.$toast.fromError(error, `Unable to update this customer's data.`);
                }
            },
            sendEmailToCustomer({subject, text}) {
                const email = new EmailMessageModel({
                    to: [this.customer.email],
                    from: this.$store.getters['user/email'],
                    subject,
                    text,
                    metadata: {
                        customerId: this.customer.id,
                        eventType: 'direct-email-sent',
                    },
                    attachments: [],
                });
                (async () => {
                    const data = email.getPayload();
                    try {
                        const message = await this.$api(({emailMessages}) => emailMessages.create({data}));
                        await this.$api(({emailMessages}) => emailMessages.send({id: message.fields.id}));
                    } catch (error) {
                        this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                    }
                })();
            },
            async updateLeadSourcesInfo(leadSources) {
                try {
                    await this.updateLeadSources(leadSources);
                    this.$toast.positive('Lead sources updated');
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to update lead sources.');
                }
            },
            async addBlacklistsItem(blacklistItem) {
                try {
                    await this.addBlacklist(blacklistItem);
                    this.$toast.positive('Blacklist added');
                } catch (err) {
                    this.$toast.fromError(err, `Can't add blacklist`);
                } finally {
                    this.$emit('close');
                }
            },
        },
    };
</script>
